import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import queryClient from "@/query-client";
import { DEFAULT_STALE_TIME } from "@/constants";
import { AXIS_KEYS } from "@/constants/ab-tests";
import type {
  ABTestStatsResponse,
  ActivityLogEntry,
  AxisFingerprint,
  AxisLayer,
  FieldZone,
  ZoneAlternative,
  GenerateVariantBResponse,
  ApplyBrandSignalResponse,
  ApplyKeywordResponse,
  ApplyScorecardGapResponse,
  GenerateRationaleResponse,
  FormatTreatmentResponse,
} from "@/types/ab-tests";
import type { AiValidationResult } from "@/types/ai-validation";

export const useGetBrandAbTestStats = (brandId: number | null | undefined) => {
  return useQuery({
    queryKey: ["brand", "ab-test-stats", brandId],
    queryFn: () => apiService.get<ABTestStatsResponse>(`/brands/${brandId}/ab-test-stats`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
  });
};

export const useUploadPastTest = () => {
  return useMutation({
    mutationFn: async (params: {
      productId: number;
      brandId: number;
      file: File;
      name?: string;
      testType?: string;
      contentType?: string;
      hypothesis?: string;
      outcome?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      const formData = new FormData();
      formData.append("brand_id", String(params.brandId));
      formData.append("file", params.file);
      if (params.name) formData.append("name", params.name);
      if (params.testType) formData.append("test_type", params.testType);
      if (params.contentType) formData.append("content_type", params.contentType);
      if (params.hypothesis) formData.append("hypothesis", params.hypothesis);
      if (params.outcome) formData.append("outcome", params.outcome);
      if (params.startDate) formData.append("start_date", params.startDate);
      if (params.endDate) formData.append("end_date", params.endDate);
      return apiService.post<{ id: string; message: string; outcome: string; hasParsedMetrics: boolean }, FormData>(
        `/products/hero/${params.productId}/ab-tests/upload-past-test`,
        formData,
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product", "ab-tests"] });
      queryClient.invalidateQueries({ queryKey: ["brand", "ab-tests", variables.brandId] });
      queryClient.invalidateQueries({ queryKey: ["brand", "ab-test-stats", variables.brandId] });
    },
  });
};

export const useStoreHypothesisMatrix = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      testId: string;
      brandId: number;
      votes: Array<{
        vote_id: string;
        type: string;
        instance: string;
        rationale: string;
        present_in: "A" | "B";
        weight?: number;
      }>;
    }) =>
      apiService.post<{ message: string }>(
        `/products/hero/${params.productId}/ab-tests/${params.testId}/hypothesis-matrix`,
        { brand_id: params.brandId, votes: params.votes },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product", "ab-tests"] });
      queryClient.invalidateQueries({ queryKey: ["brand", "ab-tests", variables.brandId] });
    },
  });
};

export const useAddActivityLogComment = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      testId: string;
      brandId: number;
      message: string;
      details?: string;
    }) =>
      apiService.post<ActivityLogEntry>(
        `/products/hero/${params.productId}/ab-tests/${params.testId}/activity-log`,
        { brand_id: params.brandId, message: params.message, details: params.details },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["product", "ab-tests"] });
      queryClient.invalidateQueries({ queryKey: ["brand", "ab-tests", variables.brandId] });
    },
  });
};

export const useExtractProductName = () => {
  return useMutation({
    mutationFn: (params: { productId: number; brandId: number }) =>
      apiService.post<{ productName: string }>(
        `/products/hero/${params.productId}/ab-tests/ai/extract-product-name`,
        { brand_id: params.brandId },
      ),
  });
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function finiteNumberOrZero(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function normalizeAxisLayer(raw: unknown): AxisLayer {
  if (!isPlainObject(raw)) return { locked: 0, descriptor: 0 };

  const layer: AxisLayer = {
    locked: finiteNumberOrZero(raw.locked),
    descriptor: finiteNumberOrZero(raw.descriptor),
  };
  if (typeof raw.evidence === "string") {
    layer.evidence = raw.evidence;
  }
  return layer;
}

/**
 * AUT-430 (WEB-013, `components/ab-test.md § The fingerprint is made whole where it enters`):
 * `detect-fingerprint` returns the model's output unchecked and caches it for 7 days. The sandbox
 * fake's generic default has no axis keys at all, and production Gemini can return the same
 * malformed shape. This makes the response whole -- pure, reads nothing but its argument, never
 * mutates it, and drops every key the declared type does not carry (`validation`, or anything
 * else) so the runtime value matches the type exactly.
 */
export function normalizeAxisFingerprint(raw: unknown): { fingerprint: AxisFingerprint; summary: string } {
  const source = isPlainObject(raw) ? raw : {};
  const rawFingerprint = isPlainObject(source.fingerprint) ? source.fingerprint : {};

  const fingerprint = AXIS_KEYS.reduce((acc, axis) => {
    acc[axis] = normalizeAxisLayer(rawFingerprint[axis]);
    return acc;
  }, {} as AxisFingerprint);

  const summary = typeof source.summary === "string" ? source.summary : "";

  return { fingerprint, summary };
}

export const useDetectFingerprint = () => {
  return useMutation({
    mutationFn: (params: { productId: number; brandId: number }) =>
      apiService
        .post<unknown, { brand_id: number }>(
          `/products/hero/${params.productId}/ab-tests/ai/detect-fingerprint`,
          { brand_id: params.brandId },
        )
        .then(normalizeAxisFingerprint),
  });
};

export const useParseFieldZones = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      fieldLabel: string;
      fieldText: string;
    }) =>
      apiService.post<{ zones: FieldZone[] }>(
        `/products/hero/${params.productId}/ab-tests/ai/parse-field-zones`,
        { brand_id: params.brandId, field_label: params.fieldLabel, field_text: params.fieldText },
      ),
  });
};

export const useZoneAlternatives = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      fieldLabel: string;
      fieldText: string;
      zoneText: string;
      axis: string;
      alsoCarries?: string[];
      availableKeywords?: string[];
    }) =>
      apiService.post<{ alternatives: ZoneAlternative[] }>(
        `/products/hero/${params.productId}/ab-tests/ai/zone-alternatives`,
        {
          brand_id: params.brandId,
          field_label: params.fieldLabel,
          field_text: params.fieldText,
          zone_text: params.zoneText,
          axis: params.axis,
          also_carries: params.alsoCarries ?? [],
          available_keywords: params.availableKeywords ?? [],
        },
      ),
  });
};

export const useZoneKeywordIntegration = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      fieldLabel: string;
      fieldText: string;
      zoneText: string;
      axis: string;
      keyword: string;
    }) =>
      apiService.post<{ phrasing: string; rationale: string }>(
        `/products/hero/${params.productId}/ab-tests/ai/zone-keyword-integration`,
        {
          brand_id: params.brandId,
          field_label: params.fieldLabel,
          field_text: params.fieldText,
          zone_text: params.zoneText,
          axis: params.axis,
          keyword: params.keyword,
        },
      ),
  });
};

export const useGenerateVariantB = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      selectedFields: string[];
      levers: string;
      hypothesis: string;
      targetKeyword?: string;
    }) =>
      apiService.post<GenerateVariantBResponse>(
        `/products/hero/${params.productId}/ab-tests/ai/generate-variant`,
        {
          brand_id: params.brandId,
          selected_fields: params.selectedFields,
          levers: params.levers,
          hypothesis: params.hypothesis,
          target_keyword: params.targetKeyword || "",
        },
      ),
  });
};

export const useApplyBrandSignal = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      selectedFields: string[];
      signalContext: string;
    }) =>
      apiService.post<ApplyBrandSignalResponse>(
        `/products/hero/${params.productId}/ab-tests/ai/apply-brand-signal`,
        {
          brand_id: params.brandId,
          selected_fields: params.selectedFields,
          signal_context: params.signalContext,
        },
      ),
  });
};

export const useApplyKeyword = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      selectedFields: string[];
      keywordContext: string;
    }) =>
      apiService.post<ApplyKeywordResponse>(
        `/products/hero/${params.productId}/ab-tests/ai/apply-keyword`,
        {
          brand_id: params.brandId,
          selected_fields: params.selectedFields,
          keyword_context: params.keywordContext,
        },
      ),
  });
};

export const useApplyScorecardGap = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      selectedFields: string[];
      gapContext: string;
    }) =>
      apiService.post<ApplyScorecardGapResponse>(
        `/products/hero/${params.productId}/ab-tests/ai/apply-scorecard-gap`,
        {
          brand_id: params.brandId,
          selected_fields: params.selectedFields,
          gap_context: params.gapContext,
        },
      ),
  });
};

export const useGenerateRationale = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      hypothesis?: string;
      leversApplied?: string;
      variantAContent: string;
      variantBContent: string;
    }) =>
      apiService.post<GenerateRationaleResponse>(
        `/products/hero/${params.productId}/ab-tests/ai/generate-rationale`,
        {
          brand_id: params.brandId,
          hypothesis: params.hypothesis,
          levers_applied: params.leversApplied,
          variant_a_content: params.variantAContent,
          variant_b_content: params.variantBContent,
        },
      ),
  });
};

export const useApplyFormatTreatment = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      selectedFields: string[];
      treatment: "lead-phrase" | "length" | "hyphenation";
      treatmentValue: string;
    }) =>
      apiService.post<FormatTreatmentResponse>(
        `/products/hero/${params.productId}/ab-tests/ai/apply-format-treatment`,
        {
          brand_id: params.brandId,
          selected_fields: params.selectedFields,
          treatment: params.treatment,
          treatment_value: params.treatmentValue,
        },
      ),
  });
};

export const useValidateAbTestOutput = () => {
  return useMutation({
    mutationFn: (params: {
      productId: number;
      brandId: number;
      output: string;
      featureKey: string;
    }) =>
      apiService.post<AiValidationResult>(
        `/products/hero/${params.productId}/ab-tests/ai/validate`,
        {
          brand_id: params.brandId,
          output: params.output,
          feature_key: params.featureKey,
        },
      ),
  });
};

